import { ReceiptLongOutlined as ReceiptLongOutlinedIcon } from 'components/icons';
import EmptyState from 'components/EmptyState';
import PartyName from 'components/party/PartyName';
import LoadMoreButton from 'components/tables/LoadMoreButton';
import SearchBar from 'components/tables/list-table/filter-pieces/SearchBar';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { useLoadMoreFeed } from 'hooks/useLoadMoreFeed';
import { InvoiceIconGroup, InvoiceMatter, InvoiceStatus } from 'pages/invoices/InvoiceTableCells';
import { getInvoiceTotalBeforePrepayment } from 'pages/invoices/util/invoiceUtil';
import { getLinks } from 'pages/matters/util';
import { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLazyGetInvoicesWithFiltersQuery } from 'store/api/invoices';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { escapeRsqlValue } from 'utils/rsql';
import { useClassifiers } from 'utils/useClassifiers';
import { GridLoader } from './CasesTab';

const PAGE_SIZE = 50;

/**
 * Drafts waiting on the user as responsible lawyer (or as supervising partner, when the tenant notifies them of drafts),
 * and reviewed invoices waiting on them as supervising partner.
 */
export const MY_PENDING_INVOICES_FILTER = 'id=ismypendinginvoice=true';

const composeSearchRsqlFilter = (search: string): string =>
    `invoiceNumber=lk='${search}',clientParty.name=lk='${search}',invoiceMatters.matter.clientParty.name=lk='${search}',invoiceMatters.matter.caseNumber=lk='${search}',invoiceMatters.matter.name=lk='${search}',invoiceMatters.matter.primaryContactParty.name=lk='${search}'`;

const USED_CLASSIFIERS = ['INVOICE_TYPE', 'INVOICE_STATUS_TYPE'];

const MyPendingInvoicesTab: FC = () => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    const [searchText, setSearchText] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [fetchData, { currentData, isFetching, isError }] = useLazyGetInvoicesWithFiltersQuery();

    const fetchPage = useCallback(
        (page: number) => {
            const filter = appliedSearch ? composeSearchRsqlFilter(escapeRsqlValue(appliedSearch.trim())) : '';
            return fetchData({
                pageSize: PAGE_SIZE,
                page,
                filters: [MY_PENDING_INVOICES_FILTER, filter ? `(${filter})` : undefined].filter(Boolean).join(';'),
            });
        },
        [fetchData, appliedSearch]
    );

    const { handleLoadMore } = useLoadMoreFeed({ fetchPage, isLast: currentData?.last });

    // A zero-row response to a search is "no matches", not "no invoices" — and it needs a way back.
    const isSearching = appliedSearch.trim().length > 0;
    const handleClearSearch = () => {
        setSearchText('');
        // Supersede any keystroke still sitting in the debounce: `useDebouncedCallback` clears its
        // pending timeout on every call, so passing '' replaces the queued value instead of letting
        // it fire 600ms later and restore `appliedSearch` behind an already-emptied search box.
        onSearchInputChange('');
        setAppliedSearch('');
    };
    const handleRetry = () => {
        void fetchPage(0);
    };

    const onSearchInputChange = useDebouncedCallback((value: string) => {
        setAppliedSearch(value);
    }, 600);

    const invoicesCount = currentData?.totalElements || 0;

    return (
        <div className="mb-5 pt-4">
            <SearchBar
                value={searchText}
                onChange={(e) => {
                    setSearchText(e.target.value);
                    onSearchInputChange(e.target.value);
                }}
                placeHolder={t('search')}
                className="mx-5 w-[calc(100%-40px)]"
            />
            {/* No count until this search has one, and a blank line meanwhile so the rows do not jump. */}
            <p className="text-body1 px-5 pt-1.5 pb-2.5">
                {currentData ? (
                    <FormattedMessage
                        id="dashboard.n-invoices"
                        values={{
                            count: invoicesCount,
                            element: (
                                <span key="element" className="font-semibold">
                                    {invoicesCount}
                                </span>
                            ),
                        }}
                    />
                ) : (
                    <>&nbsp;</>
                )}
            </p>
            {/* Order matters: a failed request and a request still in flight must NOT read as "no
                invoices". `currentData` holds this search's rows only (RTK's `data` keeps the previous
                search's), so a new search draws the skeleton and a failed one the error, while Load more,
                which shares the search's cache entry, keeps the rows. `!currentData` also covers the
                frame before the mount effect fires the lazy query. */}
            {!currentData?.content.length && isError ? (
                <EmptyState size="panel" variant="error" onRetry={handleRetry} surface="dashboard-pending-invoices" />
            ) : !currentData?.content.length && !isFetching && currentData ? (
                <EmptyState
                    size="panel"
                    variant={isSearching ? 'filtered' : 'empty'}
                    icon={ReceiptLongOutlinedIcon}
                    titleId="dashboard.empty.pending-invoices-title"
                    bodyId="dashboard.empty.pending-invoices-body"
                    surface="dashboard-pending-invoices"
                    onClearFilters={handleClearSearch}
                />
            ) : null}
            {((isFetching || !currentData) && !currentData?.content.length && !isError) || isLoadingClassifiers ? (
                <GridLoader />
            ) : (
                <ul>
                    {currentData?.content.map((invoice, index) => {
                        const isLast = index === (currentData?.content?.length || 0) - 1;
                        const invoiceMatterClients = invoice.invoiceMattersClientParties ?? [];
                        const hasDifferingInvoiceClient =
                            invoiceMatterClients.length > 1 ||
                            (invoiceMatterClients.length === 1 &&
                                invoiceMatterClients.at(0)?.id !== invoice.clientParty.id);
                        const clientLinks = getLinks('contact', invoiceMatterClients, {
                            showPartyNumber: true,
                            useLinkWithOrigin: true,
                        });
                        return (
                            <li
                                key={invoice.id}
                                className={cn(
                                    'border-divider relative flex w-full items-center',
                                    !isLast && 'border-b',
                                    index === 0 && 'mt-1 border-t'
                                )}
                            >
                                <div className={cn('flex w-full justify-between gap-2 p-4', isLast && 'pb-0')}>
                                    <div className="flex">
                                        <InvoiceIconGroup
                                            typeCode={invoice.invoiceTypeCode}
                                            typeLabel={getClassifierText(
                                                classifiers['INVOICE_TYPE'],
                                                invoice.invoiceTypeCode
                                            )}
                                            isJointInvoice={invoice.matters.length > 1}
                                            isSplit={invoice.isSplit ?? false}
                                        />
                                        <div className="flex flex-col">
                                            <div className="flex gap-[1ch]">
                                                <LinkWithOrigin
                                                    to={`/invoice/${invoice.id}`}
                                                    className="text-fg hover:text-link w-fit font-semibold no-underline hover:underline"
                                                >
                                                    {invoice.invoiceNumber}
                                                </LinkWithOrigin>
                                                {invoice.invoiceDate}
                                            </div>
                                            <div className="flex gap-[1ch]">
                                                <InvoiceMatter invoice={invoice} className="font-semibold" />
                                                <p className="text-body1">{clientLinks}</p>
                                            </div>
                                            {hasDifferingInvoiceClient && (
                                                <p className="text-body1 font-semibold">
                                                    <FormattedMessage
                                                        id="invoices.payer"
                                                        values={{
                                                            client: (
                                                                <PartyName
                                                                    key="client"
                                                                    party={invoice.clientParty}
                                                                    isLink
                                                                    useLinkWithOrigin
                                                                    variant="readonly-other"
                                                                    showPartyNumber
                                                                />
                                                            ),
                                                        }}
                                                    />
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-[1ch] self-center">
                                        <MoneyDisplay
                                            amount={getInvoiceTotalBeforePrepayment(invoice)}
                                            currency={invoice.paymentCurrencyTypeCode}
                                        />
                                        <InvoiceStatus invoice={invoice} classifiers={classifiers} />
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
            {currentData && !currentData.last ? <LoadMoreButton onClick={handleLoadMore} loading={isFetching} /> : null}
        </div>
    );
};

export default MyPendingInvoicesTab;
